import React from 'react';
import SAMLSettingsGroups from './SAMLSettingsGroups';
import Toggle from '../../Toggle';
import translations from '../../../translations';
import ClipboardInput from '../../ClipboardInput';

import { connect } from 'react-redux';
import { changeSecuritySetting, changeSAMLSetting } from '../../../actions/reduxActions/currentTenant';
import { RUNTIME_URI } from '../../../constants';

/**
 * @param {Object} SAMLSettings
 * @param {boolean} SAMLSettings.isActive - True if the user has checked Enable SAML Single Sign-On
 * @param {Object} SAMLSettings.settings - These are the SAML settings for the Tenant
 * @param {string} SAMLSettings.tenantId - TenantId to configure SAML SSO for
 * @param {Object} SAMLSettings.userClaims - Information about the current user's authentication method
 * @param {Function} SAMLSettings.changeSAMLSetting - Changes one of the SAML settings for the Tenant
 * @param {Function} SAMLSettings.changeSecuritySetting - Changes one of the security settings for the Tenant
 * @description Let the user set up an IdP to authenticate to this Tenant
 * following SAML SSO protocol
*/
export const SAMLSettings = ({
    isActive,
    settings,
    tenantId,
    userClaims,
    changeSAMLSetting,
    changeSecuritySetting,
}) => {

    const onChangeCertificate = e => changeSAMLSetting({
        settingPath: ['certificate'],
        value: e.currentTarget.value,
    });

    const onChangeRedirectUri = e => changeSAMLSetting({
        settingPath: ['redirectUri'],
        value: e.currentTarget.value,
    });

    const onChangeLogoutUri = e => changeSAMLSetting({
        settingPath: ['logoutUri'],
        value: e.currentTarget.value,
    });

    const onChangeFirstNameClaim = e => changeSAMLSetting({
        settingPath: ['claims', 'firstName'],
        value: e.currentTarget.value,
    });

    const onChangeLastNameClaim = e => changeSAMLSetting({
        settingPath: ['claims', 'lastName'],
        value: e.currentTarget.value,
    });

    const onChangeEmailClaim = e => changeSAMLSetting({
        settingPath: ['claims', 'emailAddress'],
        value: e.currentTarget.value,
    });

    const onChangeGroupClaim = e => changeSAMLSetting({
        settingPath: ['groupSettings', 'groupAttribute'],
        value: e.currentTarget.value,
    });

    const onToggleEnabled = () => {
        changeSecuritySetting({
            settingName: 'isSamlEnabled',
            value: !isActive,
        });
    };

    let ssoAlert;
    if (userClaims && userClaims.sso_tenant) {
        ssoAlert = (
            <div className="alert alert-warning">
                <strong>Warning:</strong>
                {' '}
                {translations.SAML_warning}
            </div>
        );
    }

    const SAMLLoginURL = `${RUNTIME_URI}/api/draw/1/authentication/saml/${tenantId}`;

    return (
        <React.Fragment>
            <div className="form-group">
                <Toggle 
                    isOn={isActive} 
                    onChange={onToggleEnabled}
                    className="enable-SAML"
                />
                <label>{translations.SAML_enable_label}</label>                
            </div>

            {
                isActive ?
                    (
                        <div className="SAML-settings">
                            { ssoAlert }
                            <div className="form-group">
                                <label>{translations.SAML_login_url_label}</label>
                                <ClipboardInput value={SAMLLoginURL} buttonTitle={translations.copy_saml_login_url_title} />
                                <p className="help-block">{translations.SAML_login_url_help}</p>
                            </div>
                            
                            <div className="form-group">
                                <label>{translations.SAML_certificate_label}</label>
                                <span className="input-required"> *</span>
                                <textarea
                                    className="form-control certificate"
                                    onChange={onChangeCertificate}
                                    rows={12}
                                    value={settings && settings.certificate}
                                />

                                <p className="help-block">{translations.SAML_certificate_help}</p>
                            </div>

                            <div className="form-group">
                                <label>{translations.SAML_redirect_url_label}</label>
                                <span className="input-required"> *</span>
                                <input
                                    className="form-control saml-redirect-uri"
                                    onChange={onChangeRedirectUri}
                                    type="text"
                                    value={settings && settings.redirectUri}
                                />

                                <p className="help-block">{translations.SAML_redirect_url_help}</p>
                            </div>

                            <div className="form-group">
                                <label>{translations.SAML_logout_url_label}</label>
                                <input
                                    className="form-control saml-logout-uri"
                                    onChange={onChangeLogoutUri}
                                    type="text"
                                    value={settings && settings.logoutUri}
                                />
                            </div>

                            <h4>Configuring SAML claims</h4>
                            <div className="form-group">
                                <label>{translations.SAML_firstname_label}</label>

                                <input
                                    className="form-control saml-firstname-claim"
                                    onChange={onChangeFirstNameClaim}
                                    type="text"
                                    value={settings && settings.claims && settings.claims.firstName}
                                />

                                <p className="help-block">{translations.SAML_firstname_help}</p>
                            </div>

                            <div className="form-group">
                                <label>{translations.SAML_lastname_label}</label>

                                <input
                                    className="form-control saml-lastname-claim"
                                    onChange={onChangeLastNameClaim}
                                    type="text"
                                    value={settings && settings.claims && settings.claims.lastName}
                                />

                                <p className="help-block">{translations.SAML_lastname_help}</p>
                            </div>

                            <div className="form-group">
                                <label>{translations.SAML_email_label}</label>

                                <input
                                    className="form-control saml-email-claim"
                                    onChange={onChangeEmailClaim}
                                    type="text"
                                    value={settings && settings.claims && settings.claims.emailAddress}
                                />

                                <p className="help-block">{translations.SAML_email_help}</p>
                            </div>

                            <div className="form-group">
                                <label>{translations.SAML_groupname_label}</label>

                                <input
                                    className="form-control saml-group-claim"
                                    onChange={onChangeGroupClaim}
                                    type="text"
                                    value={settings && settings.groupSettings && settings.groupSettings.groupAttribute}
                                />

                                <p className="help-block">{translations.SAML_groupname_help}</p>
                            </div>

                            <SAMLSettingsGroups settings={settings} />
                        </div>
                    ) : null
            }
        </React.Fragment>
    );
};

const mapStateToProps = state => ({
    tenantId: state.currentTenant.id,
    userClaims: state.currentUser.claims,
});

const mapDispatchToProps = {
    changeSecuritySetting,
    changeSAMLSetting,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(SAMLSettings);
